/**
 * Everspring — Wishlist drawer
 *
 * Bottom sheet on mobile, side drawer on desktop.
 * Items are stored in localStorage (key: esWishlist).
 * On open, fetch each product via /products/{handle}.json and render
 * compact horizontal cards.
 *
 * Triggered via [data-es-wishlist-open] buttons.
 * Closed via [data-es-wishlist-close] buttons + backdrop + Escape key.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'esWishlist';
  var sheet = document.querySelector('[data-es-wishlist-sheet]');
  if (!sheet) return;

  var grid = sheet.querySelector('[data-es-wishlist-grid]');
  var loading = sheet.querySelector('[data-es-wishlist-loading]');
  var emptyEl = sheet.querySelector('[data-es-wishlist-empty]');
  var countEl = sheet.querySelector('[data-es-wishlist-count]');

  var lastFocus = null;
  var loaded = false;

  /* ───── Storage helpers ───── */
  function getWishlist() {
    try {
      var raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      // Normalize legacy data: array of strings → array of {id, handle}
      if (raw.length > 0 && typeof raw[0] === 'string') {
        raw = raw.map(function (id) { return { id: id, handle: '' }; });
      }
      return raw;
    } catch (_) {
      return [];
    }
  }

  function saveWishlist(list) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch (_) {}
  }

  function removeFromWishlist(handle) {
    var list = getWishlist().filter(function (item) { return item.handle !== handle; });
    saveWishlist(list);
    document.dispatchEvent(new CustomEvent('es:wishlist:changed', { detail: { count: list.length } }));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { count: list.length } }));
    updateCount();
  }

  function updateCount() {
    var count = getWishlist().length;
    if (!countEl) return;
    if (count > 0) {
      countEl.textContent = String(count);
      countEl.hidden = false;
    } else {
      countEl.hidden = true;
    }
  }

  /* ───── Open / close ───── */
  function openSheet() {
    sheet.hidden = false;
    requestAnimationFrame(function () {
      sheet.setAttribute('aria-hidden', 'false');
    });
    document.body.classList.add('es-wishlist-sheet-open');
    lastFocus = document.activeElement;
    var closeBtn = sheet.querySelector('[data-es-wishlist-close]');
    if (closeBtn && closeBtn.focus) {
      try { closeBtn.focus({ preventScroll: true }); } catch (_) { closeBtn.focus(); }
    }
    if (!loaded) loadItems();
  }

  function closeSheet() {
    sheet.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('es-wishlist-sheet-open');
    setTimeout(function () { sheet.hidden = true; }, 320);
    if (lastFocus && lastFocus.focus) {
      try { lastFocus.focus({ preventScroll: true }); } catch (_) { lastFocus.focus(); }
    }
  }

  /* ───── Trigger wiring ───── */
  document.addEventListener('click', function (e) {
    var openTrigger = e.target.closest && e.target.closest('[data-es-wishlist-open]');
    if (openTrigger) {
      e.preventDefault();
      openSheet();
      return;
    }
    var closeTrigger = e.target.closest && e.target.closest('[data-es-wishlist-close]');
    if (closeTrigger && sheet.contains(closeTrigger) === false) {
      // close button outside sheet (e.g. backdrop click via delegate)
    }
    if (closeTrigger) {
      e.preventDefault();
      closeSheet();
    }
    var removeBtn = e.target.closest && e.target.closest('[data-es-wishlist-remove]');
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();
      var handle = removeBtn.getAttribute('data-handle');
      var card = removeBtn.closest('.es-product-card');
      if (card) {
        card.classList.add('es-product-card--removing');
        setTimeout(function () {
          if (handle) removeFromWishlist(handle);
          card.parentNode.removeChild(card);
          if (grid.children.length === 0) {
            grid.hidden = true;
            emptyEl.hidden = false;
          }
        }, 280);
      }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sheet.getAttribute('aria-hidden') === 'false') {
      closeSheet();
    }
  });

  /* ───── Render ───── */
  function fetchProductByHandle(handle) {
    if (!handle) return Promise.resolve(null);
    return fetch('/products/' + handle + '.json')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { return d && d.product ? d.product : null; })
      .catch(function () { return null; });
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
  }

  function formatMoney(amount) {
    var num = parseFloat(amount);
    if (isNaN(num)) return '';
    return '\u20AC' + num.toFixed(2).replace('.', ',');
  }

  function getSizedImageUrl(url, size) {
    if (!url) return '';
    var match = url.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
    if (match) return url.replace(match[0], '_' + size + '.' + match[1] + (match[2] || ''));
    return url;
  }

  function buildCard(product) {
    var handle = product.handle;
    var title = product.title;
    var url = '/products/' + handle;
    var firstVariant = product.variants && product.variants[0];
    var price = firstVariant ? firstVariant.price : '0.00';
    var comparePrice = firstVariant ? firstVariant.compare_at_price : null;
    var image = product.image ? product.image.src : (product.images && product.images[0] ? product.images[0].src : '');
    var available = firstVariant ? (firstVariant.available !== false) : (product.available !== false);
    var variantId = firstVariant ? firstVariant.id : '';

    var hasSale = comparePrice && parseFloat(comparePrice) > parseFloat(price);
    var discountPct = hasSale ? Math.round(((parseFloat(comparePrice) - parseFloat(price)) / parseFloat(comparePrice)) * 100) : 0;

    var imgHtml = image
      ? '<img src="' + getSizedImageUrl(image, '300x') + '" alt="' + escapeHtml(title) + '" loading="lazy">'
      : '<div class="es-product-card__placeholder"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg></div>';

    var badgeHtml = hasSale
      ? '<span class="es-product-card__badge">-' + discountPct + '%</span>'
      : (available ? '' : '<span class="es-product-card__badge">Uitverkocht</span>');

    var priceHtml = hasSale
      ? '<span class="es-product-card__price--sale">' + formatMoney(price) + '</span><span class="es-product-card__compare">' + formatMoney(comparePrice) + '</span>'
      : '<span class="es-product-card__price">' + formatMoney(price) + '</span>';

    var atcHtml = available
      ? '<button class="es-product-card__atc-btn" type="button" data-variant-id="' + variantId + '" data-atc-btn>In winkelmand</button>'
      : '<button class="es-product-card__atc-btn es-product-card__atc-btn--disabled" disabled type="button">Uitverkocht</button>';

    var removeBtnHtml = '';

    return (
      '<div class="es-product-card" data-product-handle="' + escapeHtml(handle) + '">' +
        '<div class="es-product-card__media">' +
          '<a href="' + url + '" aria-label="' + escapeHtml(title) + '">' + imgHtml + '</a>' +
          badgeHtml +
          '<button type="button" class="es-product-card__wishlist" data-es-wishlist-remove data-handle="' + escapeHtml(handle) + '" aria-label="Verwijder uit wishlist">' +
            '<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="es-product-card__info">' +
          '<a href="' + url + '" class="es-product-card__title">' + escapeHtml(title) + '</a>' +
          '<div class="es-product-card__price-row">' + priceHtml + '</div>' +
          '<div class="es-product-card__atc es-wishlist-card__atc-row">' +
            atcHtml +
            removeBtnHtml +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function loadItems() {
    var list = getWishlist();
    if (list.length === 0) {
      emptyEl.hidden = false;
      grid.hidden = true;
      loading.hidden = true;
      loaded = true;
      return;
    }
    var withHandles = list.filter(function (i) { return i.handle; });
    if (withHandles.length === 0) {
      emptyEl.hidden = false;
      grid.hidden = true;
      loading.hidden = true;
      loaded = true;
      return;
    }

    emptyEl.hidden = true;
    grid.hidden = true;
    loading.hidden = false;

    Promise.all(withHandles.map(function (item) { return fetchProductByHandle(item.handle); }))
      .then(function (products) {
        var valid = products.filter(function (p) { return p !== null; });
        if (valid.length === 0) {
          emptyEl.hidden = false;
          grid.hidden = true;
          loading.hidden = true;
          loaded = true;
          return;
        }
        grid.innerHTML = valid.map(buildCard).join('');
        loading.hidden = true;
        grid.hidden = false;
        loaded = true;
      });
  }

  /* ───── ATC handler (delegate) ───── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.es-wishlist-sheet__grid [data-atc-btn]');
    if (!btn) return;
    e.preventDefault();
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;
    btn.disabled = true;
    btn.textContent = 'Toegevoegd…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        btn.textContent = '✓ Toegevoegd';
        document.dispatchEvent(new CustomEvent('cart:updated'));
        // Refresh cart drawer state
        if (window.PUB_SUB_EVENTS) {
          // Dawn pub-sub: optional
        }
        setTimeout(function () { btn.disabled = false; btn.textContent = 'In winkelmand'; }, 1500);
      })
      .catch(function () {
        btn.textContent = 'Fout, opnieuw?';
        btn.disabled = false;
      });
  });

  /* ───── Init: count badge ───── */
  updateCount();
  document.addEventListener('es:wishlist:changed', updateCount);

  // Reload items if storage changes (e.g. user added one elsewhere then opens drawer)
  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) {
      loaded = false;
      updateCount();
      if (sheet.getAttribute('aria-hidden') === 'false') loadItems();
    }
  });
})();
